import React, { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Loader2, Monitor, Moon, Palette, Sun } from 'lucide-react';
import { API_BASE_URL } from '../config/api';
import { DirtyForm, useDirtyForm } from '../../hooks/useDirtyForm';
import { registerDirtySource } from '../../utils/unsavedChanges';
import {
  BRAND_SWATCHES, DEFAULT_BRAND, DEFAULT_SURFACE, ResolvedStoreTheme, StoreTheme, ThemeAppearance, ThemeSurface, THEME_SURFACES,
  applyStoreTheme, deriveThemeCss, deriveThemeTokens, hexToHsl, hslToHex, isBrandHex, isDefaultBrand,
  normalizeTheme, resolveTheme, textOn,
} from '../../utils/storeTheme';
import { SheetInherit, SheetSection } from './sheet';

/**
 * «Εμφάνιση» — απόφαση ιδιοκτήτη 12/10/2026 (migration 263): ο οργανισμός ορίζει την προεπιλογή, κάθε
 * κατάστημα μπορεί να την αλλάξει ανά πεδίο. Τρία πεδία: χρώμα (το ΕΝΑ χρώμα ενέργειας), φόντο του
 * ανοιχτού θέματος, προεπιλογή ανοιχτό/σκούρο για συσκευή χωρίς δική της επιλογή.
 *
 * Δύο μορφές:
 *  - 'sheet' (ελεγχόμενη) — Κατάστημα → «Εμφάνιση» (13/10/2026): τρεις ενότητες του «Φύλλου». Το
 *    πρόχειρο το κρατά η σελίδα (useStoreThemeForm) και το σώζει η ΜΙΑ μπάρα της, μαζί με γλώσσα και
 *    οθόνη πελάτη. «Όπως ο οργανισμός»/«Προεπιλογή εφαρμογής» ανά πεδίο· «Χρήση των ρυθμίσεων
 *    οργανισμού / της προεπιλογής εφαρμογής» = όλα null, επίσης με την μπάρα.
 *  - 'embedded' (αυτόνομη) — Κεντρικά → Ρυθμίσεις (OrgThemeSettings): δικό της «Αποθήκευση» (aria-label
 *    «Αποθήκευση εμφάνισης»). Εκεί το μπλε και το «Χαρτί» ΕΙΝΑΙ η προεπιλογή εφαρμογής (null).
 * Στη συσκευή που αποθηκεύει ισχύει αμέσως· στα ανοιχτά ταμεία μετά από ανανέωση (F5).
 */

type Scope = 'store' | 'org';

/** Το πρόχειρο της ελεγχόμενης μορφής: πάντα και τα τρία πεδία, null = κληρονομείται. */
export type ThemeDraft = ResolvedStoreTheme;

export const toThemeDraft = (theme: unknown): ThemeDraft => {
  const t = normalizeTheme(theme) || {};
  return { brand: t.brand ?? null, surface: t.surface ?? null, appearance: t.appearance ?? null };
};

interface CommonProps {
  scope: Scope;
  /** Μόνο κατάστημα: η ρύθμιση του οργανισμού (τι κληρονομεί). */
  inherited?: StoreTheme | null;
  /** Μόνο κατάστημα: ανήκει σε οργανισμό; Αλλιώς το «κληρονομεί» σημαίνει «προεπιλογή εφαρμογής». */
  hasOrg?: boolean;
  canEdit?: boolean;
  loading?: boolean;
  /** Όνομα στην προεπισκόπηση. */
  storeName?: string;
}

/** Αυτόνομη: δικό της «Αποθήκευση». */
export interface ThemeSelfSavingProps extends CommonProps {
  variant?: 'embedded';
  /** Η ρύθμιση αυτού του επιπέδου (παράκαμψη καταστήματος / προεπιλογή οργανισμού). */
  value: StoreTheme | null;
  /** Αποθηκεύει και επιστρέφει την αποθηκευμένη τιμή· Error με μήνυμα για τον χρήστη σε αποτυχία. */
  onSave: (next: StoreTheme | null) => Promise<StoreTheme | null>;
}

/** Ελεγχόμενη: ενότητες του «Φύλλου», χωρίς δικό της κουμπί. */
export interface ThemeSheetProps extends CommonProps {
  variant: 'sheet';
  draft: ThemeDraft;
  /** Αλλαγή ενός ή περισσότερων πεδίων του προχείρου. */
  onDraftChange: (patch: Partial<ThemeDraft>) => void;
  /** Άκυρος κωδικός χρώματος στο πεδίο: η σελίδα κλειδώνει την «Αποθήκευση». */
  onInvalidChange?: (invalid: boolean) => void;
  /** Πεδία με αλλαγές (τελεία στην ενότητα και στο ευρετήριο). */
  changed?: Partial<Record<keyof ThemeDraft, boolean>>;
  /** Αλλάζει σε κάθε «Αναίρεση»: το πεδίο hex ξαναγράφεται από το πρόχειρο. */
  resetKey?: number;
}

export type ThemeSettingsSectionProps = ThemeSelfSavingProps | ThemeSheetProps;

const sameTheme = (a: unknown, b: unknown) => JSON.stringify(normalizeTheme(a)) === JSON.stringify(normalizeTheme(b));

const APPEARANCES: ThemeAppearance[] = ['light', 'dark', 'system'];
const APPEARANCE_ICON: Record<ThemeAppearance, React.ElementType> = { light: Sun, dark: Moon, system: Monitor };
const swatchCheck = (hex: string) => hslToHex(textOn(hexToHsl(hex)));
const tonePreview = (brand: string, s: ThemeSurface) => {
  const tk = deriveThemeTokens({ brand, surface: s }, { full: true }).light;
  return ['--surface-0', '--surface-1', '--surface-2'].map(k => `hsl(${tk[k]})`);
};

/** Ονόματα χρωμάτων/φόντων/εμφάνισης και η ετικέτα «κληρονομεί» — κοινά στις δύο μορφές. */
function useThemeVocabulary(hasOrg: boolean) {
  const { t } = useTranslation(['settings', 'admin']);
  const swatchName: Record<string, string> = {
    default: t('settings:theme.swatch.default'),
    yellow: t('settings:theme.swatch.yellow'),
    orange: t('settings:theme.swatch.orange'),
    red: t('settings:theme.swatch.red'),
    rose: t('settings:theme.swatch.rose'),
    violet: t('settings:theme.swatch.violet'),
    green: t('settings:theme.swatch.green'),
    teal: t('settings:theme.swatch.teal'),
    cyan: t('settings:theme.swatch.cyan'),
    slate: t('settings:theme.swatch.slate'),
    black: t('settings:theme.swatch.black'),
    brown: t('settings:theme.swatch.brown'),
  };
  const surfaceName: Record<ThemeSurface, string> = {
    paper: t('settings:theme.surface.paper'),
    neutral: t('settings:theme.surface.neutral'),
    cool: t('settings:theme.surface.cool'),
    brand: t('settings:theme.surface.brand'),
  };
  const surfaceHint: Record<ThemeSurface, string> = {
    paper: t('settings:theme.surface.paperHint'),
    neutral: t('settings:theme.surface.neutralHint'),
    cool: t('settings:theme.surface.coolHint'),
    brand: t('settings:theme.surface.brandHint'),
  };
  const appearanceName: Record<ThemeAppearance, string> = {
    light: t('settings:theme.appearance.light'),
    dark: t('settings:theme.appearance.dark'),
    system: t('settings:theme.appearance.system'),
  };
  const colorLabel = (hex: string | null | undefined): string => {
    if (!hex || isDefaultBrand(hex)) return swatchName.default;
    const sw = BRAND_SWATCHES.find(s => s.hex === hex.toUpperCase());
    return sw ? swatchName[sw.id] : `${t('settings:theme.customColor')} ${hex.toUpperCase()}`;
  };
  // «Κληρονομεί»: από τον οργανισμό, ή (κατάστημα χωρίς οργανισμό) η προεπιλογή εφαρμογής.
  const inheritLabel = hasOrg ? t('settings:theme.inheritOrg') : t('settings:theme.inheritApp');
  return { t, swatchName, surfaceName, surfaceHint, appearanceName, colorLabel, inheritLabel };
}

/** Το ταμείο σε μικρογραφία, με τα tokens του θέματος πάνω του (deriveThemeCss με scope). */
const CashierPreview: React.FC<{ scopeId: string; mode: 'light' | 'dark'; storeName?: string; testId?: string; className?: string }> = ({
  scopeId, mode, storeName, testId, className = '',
}) => {
  const { t } = useTranslation(['settings']);
  return (
    <div data-theme-preview={scopeId} data-mode={mode} data-testid={testId} className={`bg-surface-1 text-t1 select-none ${className}`}
      aria-hidden="true">
      <div className="flex items-center gap-1.5 px-2.5 h-10 bg-surface-2 border-b border-line">
        <span className="w-5 h-5 rounded-md bg-t1 shrink-0" />
        <span className="text-[11px] font-semibold text-t1 truncate min-w-0">{storeName || t('settings:theme.preview.store')}</span>
        <span className="ml-auto relative px-2 h-7 inline-flex items-center rounded-md text-[11px] font-medium bg-brand-soft text-brand whitespace-nowrap">
          {t('settings:theme.preview.tabTakeaway')}
          <span className="absolute left-1.5 right-1.5 -bottom-[7px] h-0.5 rounded-full bg-brand" />
        </span>
        <span className="px-2 h-7 inline-flex items-center rounded-md text-[11px] text-t3 whitespace-nowrap">{t('settings:theme.preview.tabTables')}</span>
      </div>
      <div className="p-2.5 grid grid-cols-3 gap-1.5 bg-surface-1">
        {[t('settings:theme.preview.item1'), t('settings:theme.preview.item2'), t('settings:theme.preview.item3')].map((name, i) => (
          <span key={name} className={`rounded-lg border px-1.5 py-2 text-[11px] leading-tight text-center truncate
            ${i === 0 ? 'bg-brand-soft border-brand-line text-brand font-semibold' : 'bg-surface-2 border-line text-t1'}`}>{name}</span>
        ))}
      </div>
      <div className="mx-2.5 mb-2.5 rounded-lg bg-surface-2 border border-line p-2">
        <div className="flex justify-between gap-2 text-[11px] text-t2">
          <span className="truncate">{t('settings:theme.preview.line')}</span>
          <span className="font-semibold text-t1 tabular-nums">2,40 €</span>
        </div>
        <span className="block text-[11px] text-brand font-medium mt-0.5">{t('settings:theme.preview.note')}</span>
        <span className="mt-2 h-9 rounded-lg bg-brand text-brand-fg text-xs font-bold flex items-center justify-center">
          {t('settings:theme.preview.pay')}
        </span>
      </div>
    </div>
  );
};

/* ───────────────────────── ελεγχόμενη: ενότητες του «Φύλλου» ───────────────────────── */

// Οι μικρογραφίες σχεδιάζονται σε 336px (όπως η προεπισκόπηση) και μικραίνουν στο πλάτος της επιλογής.
const PREVIEW_W = 336;
const MINI = 'absolute left-0 top-0 w-[336px] origin-top-left [transform:scale(var(--k,0.5))]';
// Δαχτυλίδι επιλογής: το κενό ανάμεσα έχει το χρώμα του φύλλου (--xs-ring-off).
const RING_OFF = 'ring-offset-2 ring-offset-[color:hsl(var(--xs-ring-off,var(--surface-1)))]';
const SWATCH_ON = `ring-2 ring-t1 ${RING_OFF}`;
const TILE_ON = `ring-2 ring-brand ${RING_OFF}`;
const PEER_FOCUS = `peer-focus-visible:ring-2 peer-focus-visible:ring-brand peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-[color:hsl(var(--xs-ring-off,var(--surface-1)))]`;

const ThemeSheet: React.FC<ThemeSheetProps> = ({
  scope, inherited = null, hasOrg = false, canEdit = true, loading = false, storeName,
  draft, onDraftChange, onInvalidChange, changed = {}, resetKey = 0,
}) => {
  const isStore = scope === 'store';
  const { t, swatchName, surfaceName, surfaceHint, appearanceName, colorLabel, inheritLabel } = useThemeVocabulary(hasOrg);
  const uid = useId();
  const [hexText, setHexText] = useState<string>(draft.brand || '');

  // Νέο χρώμα απ' έξω (δείγμα, «Αναίρεση», φόρτωση) → το πεδίο το ακολουθεί· όσο γράφεις, μένει ό,τι γράφεις.
  useEffect(() => {
    setHexText(prev => (isBrandHex(prev.trim()) && prev.trim().toUpperCase() === (draft.brand || '') ? prev : (draft.brand || '')));
  }, [draft.brand, resetKey]);

  const hexInvalid = hexText.trim() !== '' && !isBrandHex(hexText.trim());
  useEffect(() => { onInvalidChange?.(hexInvalid); }, [hexInvalid, onInvalidChange]);
  useEffect(() => () => onInvalidChange?.(false), [onInvalidChange]);

  const parentKey = isStore ? JSON.stringify(normalizeTheme(inherited)) : 'null';
  const parent = useMemo(() => normalizeTheme(JSON.parse(parentKey)), [parentKey]);
  const effective = useMemo(() => resolveTheme(parent, draft), [parent, draft]);
  const disabled = !canEdit || loading;

  const setBrand = (hex: string | null) => { onDraftChange({ brand: hex }); setHexText(hex || ''); };
  // Οργανισμός: το μπλε και το «Στο χρώμα» (DEFAULT_SURFACE) είναι η προεπιλογή εφαρμογής → null.
  const brandForSwatch = (hex: string) => (!isStore && isDefaultBrand(hex) ? null : hex);
  const surfaceForChoice = (s: ThemeSurface) => (!isStore && s === DEFAULT_SURFACE ? null : s);
  const brandChecked = (hex: string) => (draft.brand ? draft.brand.toUpperCase() === hex.toUpperCase() : !isStore && isDefaultBrand(hex));
  const surfaceChecked = (s: ThemeSurface) => (draft.surface ? draft.surface === s : !isStore && s === DEFAULT_SURFACE);
  const customBrand = !!draft.brand && !BRAND_SWATCHES.some(s => s.hex === draft.brand!.toUpperCase());
  const anyOverride = !!(draft.brand || draft.surface || draft.appearance);

  // Προεπισκόπηση: ΟΛΑ τα tokens πάνω στις μικρογραφίες (όποιο θέμα κι αν ισχύει στη σελίδα γύρω τους).
  const previewScope = `[data-theme-preview="${uid}"]`;
  const previewCss = useMemo(() => deriveThemeCss(effective, { scope: previewScope }), [effective, previewScope]);
  const effectiveBrand = effective.brand || DEFAULT_BRAND;
  const shownBrand = draft.brand || parent?.brand || DEFAULT_BRAND;

  const shotRef = useRef<HTMLSpanElement>(null);
  const [scale, setScale] = useState(0.5);
  useLayoutEffect(() => {
    const el = shotRef.current;
    if (!el) return undefined;
    const measure = () => { if (el.clientWidth > 0) setScale(el.clientWidth / PREVIEW_W); };
    measure();
    const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    return () => ro?.disconnect();
  }, []);

  const resetLabel = isStore ? (hasOrg ? t('settings:theme.useOrg') : t('settings:theme.useApp')) : t('admin:orgThemeReset');
  const cursor = disabled ? 'cursor-not-allowed' : 'cursor-pointer';

  return (
    <>
      <style data-testid="theme-preview-css">{previewCss}</style>

      <SheetSection
        id="theme-color"
        title={t('settings:theme.brandLabel')}
        hint={t('settings:theme.brandHint')}
        dirty={!!changed.brand || hexInvalid}
        aside={isStore || loading ? (
          <>
            {isStore && (
              <SheetInherit name={`${uid}-brand`} checked={!draft.brand} disabled={disabled} onSelect={() => setBrand(null)}
                label={inheritLabel} value={colorLabel(parent?.brand)} swatch={parent?.brand || DEFAULT_BRAND} testId="theme-brand-inherit" />
            )}
            {loading && <Loader2 className="mt-3 w-4 h-4 animate-spin text-t3" aria-label={t('settings:theme.loading')} />}
          </>
        ) : undefined}
      >
        {!canEdit && (
          <p className="mb-4 rounded-lg border border-line bg-surface-2 px-3 py-2 text-xs text-t3">{t('settings:theme.readOnly')}</p>
        )}
        <fieldset disabled={disabled} className="min-w-0">
          <legend className="sr-only">{t('settings:theme.brandLabel')}</legend>
          <div className="flex flex-wrap items-center gap-x-[22px] gap-y-4">
            <div className="grid grid-cols-6 gap-2 w-max">
              {BRAND_SWATCHES.map(sw => {
                const checked = brandChecked(sw.hex);
                return (
                  <label key={sw.id} className={`relative ${cursor}`} title={`${swatchName[sw.id]} ${sw.hex}`}>
                    <input type="radio" name={`${uid}-brand`} className="peer sr-only" checked={checked} disabled={disabled}
                      aria-label={swatchName[sw.id]} onChange={() => setBrand(brandForSwatch(sw.hex))} />
                    <span aria-hidden="true"
                      className={`flex items-center justify-center w-11 h-11 rounded-xl border border-black/10 transition-transform
                        ${PEER_FOCUS} ${checked ? SWATCH_ON : 'hover:scale-105'}`}
                      style={{ background: sw.hex }}>
                      {checked && <Check className="w-5 h-5" strokeWidth={3} style={{ color: swatchCheck(sw.hex) }} />}
                    </span>
                  </label>
                );
              })}
            </div>
            <span aria-hidden="true" className="w-px self-stretch bg-line group-data-[layout=narrow]/sheet:hidden" />
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <label className={`relative w-11 h-11 rounded-xl border border-black/10 overflow-hidden shrink-0 ${cursor} ${customBrand ? SWATCH_ON : ''}`}
                  title={t('settings:theme.customColor')}>
                  <input type="color" aria-label={t('settings:theme.pickColor')}
                    className="absolute inset-0 w-full h-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
                    value={shownBrand.toLowerCase()}
                    disabled={disabled}
                    onChange={e => setBrand(e.target.value.toUpperCase())} />
                  <span aria-hidden="true" className="flex items-center justify-center w-full h-full" style={{ background: shownBrand }}>
                    {customBrand && <Check className="w-5 h-5" strokeWidth={3} style={{ color: swatchCheck(shownBrand) }} />}
                  </span>
                </label>
                <input
                  type="text"
                  inputMode="text"
                  spellCheck={false}
                  maxLength={7}
                  aria-label={t('settings:theme.hexLabel')}
                  aria-invalid={hexInvalid || undefined}
                  placeholder="#F5B700"
                  value={hexText}
                  disabled={disabled}
                  onChange={e => {
                    const v = e.target.value.trim();
                    setHexText(v);
                    if (isBrandHex(v)) onDraftChange({ brand: v.toUpperCase() });
                  }}
                  className={`h-11 w-32 px-3 rounded-xl border bg-surface-2 text-t1 font-mono text-sm uppercase placeholder-t4
                    focus:outline-none focus:ring-2 focus:ring-ring/50 ${hexInvalid ? 'border-red-500' : 'border-line-strong focus:border-ring'}`}
                />
              </div>
              <span className="text-xs text-t3">{colorLabel(effective.brand)}</span>
            </div>
          </div>
          {hexInvalid && <p className="mt-2 text-xs text-red-600 dark:text-red-400" role="alert">{t('settings:theme.hexInvalid')}</p>}
          <p className="xs-note">{t('settings:theme.brandNote')}</p>
        </fieldset>
      </SheetSection>

      <SheetSection
        id="theme-surface"
        title={t('settings:theme.surfaceLabel')}
        hint={t('settings:theme.surfaceHint')}
        dirty={!!changed.surface}
        aside={isStore ? (
          <SheetInherit name={`${uid}-surface`} checked={!draft.surface} disabled={disabled} onSelect={() => onDraftChange({ surface: null })}
            label={inheritLabel} value={surfaceName[parent?.surface || DEFAULT_SURFACE]} testId="theme-surface-inherit" />
        ) : undefined}
      >
        <fieldset disabled={disabled} className="min-w-0">
          <legend className="sr-only">{t('settings:theme.surfaceLabel')}</legend>
          <div className="grid grid-cols-4 gap-3 group-data-[layout=narrow]/sheet:grid-cols-2">
            {THEME_SURFACES.map(s => {
              const [c0, c1, c2] = tonePreview(effectiveBrand, s);
              const checked = surfaceChecked(s);
              return (
                <label key={s} className={`flex flex-col gap-2 min-w-0 ${cursor}`}>
                  <input type="radio" name={`${uid}-surface`} className="peer sr-only" checked={checked} disabled={disabled}
                    onChange={() => onDraftChange({ surface: surfaceForChoice(s) })} />
                  <span aria-hidden="true"
                    className={`relative block h-16 rounded-[10px] overflow-hidden transition-shadow ${PEER_FOCUS} ${checked ? TILE_ON : 'ring-1 ring-line hover:ring-line-strong'}`}
                    style={{ background: c0 }}>
                    <span className="absolute left-3 top-3 right-0 bottom-0 rounded-tl-[7px]" style={{ background: c1 }} />
                    <span className="absolute left-6 top-[22px] right-3 bottom-2.5 rounded-[5px] shadow-sm" style={{ background: c2 }}>
                      <span className="absolute left-2 top-2 h-1 w-[46%] rounded-sm bg-black/[.13]" />
                      <span className="absolute left-2 top-4 h-1 w-[30%] rounded-sm bg-black/[.08]" />
                      <span className="absolute right-[7px] bottom-[7px] w-6 h-[9px] rounded-[3px]" style={{ background: effectiveBrand }} />
                    </span>
                  </span>
                  <span className={`text-[13px] leading-[18px] font-semibold ${checked ? 'text-brand' : 'text-t1'}`}>{surfaceName[s]}</span>
                  {' '}
                  <span className="-mt-1 text-xs text-t3">{surfaceHint[s]}</span>
                </label>
              );
            })}
          </div>
          <p className="xs-note">{t('settings:theme.surfaceNote')}</p>
        </fieldset>
      </SheetSection>

      <SheetSection
        id="theme-mode"
        title={t('settings:theme.appearanceLabel')}
        indexLabel={t('settings:theme.appearanceIndex')}
        hint={t('settings:theme.appearanceHint')}
        dirty={!!changed.appearance}
        aside={(
          <SheetInherit name={`${uid}-appearance`} checked={!draft.appearance} disabled={disabled}
            onSelect={() => onDraftChange({ appearance: null })}
            label={isStore ? inheritLabel : t('settings:theme.appearance.app')}
            value={isStore && parent?.appearance ? appearanceName[parent.appearance] : undefined}
            testId="theme-appearance-inherit" />
        )}
      >
        <fieldset disabled={disabled} className="min-w-0">
          <legend className="sr-only">{t('settings:theme.appearanceLabel')}</legend>
          {/* Η επιλογή ΕΙΝΑΙ η προεπισκόπηση: μικρογραφία του ταμείου με τα χρώματα που διάλεξες. */}
          <div className="grid grid-cols-3 gap-3.5" style={{ '--k': scale } as React.CSSProperties}>
            {APPEARANCES.map((a, i) => {
              const Icon = APPEARANCE_ICON[a];
              const checked = draft.appearance === a;
              return (
                <label key={a} className={`flex flex-col gap-2 min-w-0 ${cursor}`}>
                  <input type="radio" name={`${uid}-appearance`} className="peer sr-only" checked={checked} disabled={disabled}
                    onChange={() => onDraftChange({ appearance: a })} />
                  <span ref={i === 0 ? shotRef : undefined} aria-hidden="true"
                    className={`relative block aspect-[336/196] rounded-[10px] overflow-hidden transition-shadow ${PEER_FOCUS} ${checked ? TILE_ON : 'ring-1 ring-line hover:ring-line-strong'}`}>
                    {a === 'system' ? (
                      <>
                        <span className="absolute inset-0"><CashierPreview scopeId={uid} mode="light" storeName={storeName} className={MINI} /></span>
                        <span className="absolute inset-0 [clip-path:polygon(60%_0,100%_0,100%_100%,40%_100%)]">
                          <CashierPreview scopeId={uid} mode="dark" storeName={storeName} className={MINI} />
                        </span>
                      </>
                    ) : (
                      <span className="absolute inset-0">
                        <CashierPreview scopeId={uid} mode={a} storeName={storeName} testId={`theme-preview-${a}`} className={MINI} />
                      </span>
                    )}
                  </span>
                  <span className={`flex items-center gap-2 text-[13px] leading-[18px] ${checked ? 'text-brand font-semibold' : 'text-t2'}`}>
                    <span className="xs-radio" data-checked={checked} aria-hidden="true" />
                    <Icon className={`w-[15px] h-[15px] shrink-0 ${checked ? '' : 'text-t3'}`} aria-hidden="true" />
                    <span>{appearanceName[a]}</span>
                  </span>
                </label>
              );
            })}
          </div>
          <p className="xs-note">
            {t('settings:theme.previewNote')}{' '}
            <button type="button" className="xs-link" disabled={!anyOverride}
              onClick={() => { onDraftChange({ brand: null, surface: null, appearance: null }); setHexText(''); }}>
              {resetLabel}
            </button>
            {' · '}
            {t('settings:theme.applyHint')}
          </p>
        </fieldset>
      </SheetSection>
    </>
  );
};

/* ───────────────────────── αυτόνομη: με δικό της «Αποθήκευση» ───────────────────────── */

/** Επιλογή-«κουμπί» με εγγενές radio (πληκτρολόγιο/αναγνώστες οθόνης δωρεάν), ύψος ≥ 44px. */
const Choice: React.FC<{
  name: string; checked: boolean; disabled?: boolean; onSelect: () => void; children: React.ReactNode;
  className?: string; testId?: string;
}> = ({ name, checked, disabled, onSelect, children, className = '', testId }) => (
  <label className={`relative block ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'} ${className}`} data-testid={testId}>
    <input type="radio" name={name} className="peer sr-only" checked={checked} disabled={disabled} onChange={onSelect} />
    <span className={`flex items-center gap-2 min-h-[44px] h-full px-3 py-2 rounded-xl border text-sm transition-colors
      peer-focus-visible:ring-2 peer-focus-visible:ring-brand peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-surface-2
      ${checked ? 'border-brand bg-brand-soft text-brand font-semibold' : 'border-line bg-surface-2 text-t1 hover:bg-surface-3'}`}>
      {children}
    </span>
  </label>
);

const ThemeSelfSaving: React.FC<ThemeSelfSavingProps> = ({
  scope, value, inherited = null, hasOrg = false, canEdit = true, loading = false, onSave, storeName,
}) => {
  const isStore = scope === 'store';
  const { t, swatchName, surfaceName, surfaceHint, appearanceName, colorLabel, inheritLabel } = useThemeVocabulary(hasOrg);
  const uid = useId();
  const [draft, setDraft] = useState<StoreTheme>(() => normalizeTheme(value) || {});
  const [hexText, setHexText] = useState<string>(() => normalizeTheme(value)?.brand || '');
  const [saving, setSaving] = useState<'save' | 'inherit' | null>(null);
  const [status, setStatus] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  // Νέα τιμή από τον server (φόρτωση/αποθήκευση) → νέο πρόχειρο.
  const valueKey = JSON.stringify(normalizeTheme(value));
  useEffect(() => {
    const v = normalizeTheme(value) || {};
    setDraft(v);
    setHexText(v.brand || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- το κλειδί είναι το περιεχόμενο
  }, [valueKey]);

  const parentKey = isStore ? JSON.stringify(normalizeTheme(inherited)) : 'null';
  const parent = useMemo(() => normalizeTheme(JSON.parse(parentKey)), [parentKey]);
  const effective = useMemo(() => resolveTheme(parent, draft), [parent, draft]);
  const dirty = !sameTheme(draft, value);
  const disabled = !canEdit || loading || saving !== null;

  // Δικό της «Αποθήκευση», αλλά ο φρουρός «έχεις αλλαγές» της διαχείρισης πρέπει να τη βλέπει κι αυτή.
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  useEffect(() => registerDirtySource(`theme-${uid}`, () => dirtyRef.current), [uid]);

  const set = (patch: Partial<StoreTheme>) => { setStatus(null); setDraft(prev => ({ ...prev, ...patch })); };
  const setBrand = (hex: string | null) => { set({ brand: hex }); setHexText(hex || ''); };

  // Οργανισμός: το μπλε και το «Στο χρώμα» (DEFAULT_SURFACE) είναι η προεπιλογή εφαρμογής → null.
  const brandForSwatch = (hex: string) => (!isStore && isDefaultBrand(hex) ? null : hex);
  const surfaceForChoice = (s: ThemeSurface) => (!isStore && s === DEFAULT_SURFACE ? null : s);
  const brandChecked = (hex: string) => {
    if (draft.brand) return draft.brand.toUpperCase() === hex.toUpperCase();
    return !isStore && isDefaultBrand(hex);
  };
  const surfaceChecked = (s: ThemeSurface) => (draft.surface ? draft.surface === s : !isStore && s === DEFAULT_SURFACE);
  const customBrand = !!draft.brand && !BRAND_SWATCHES.some(s => s.hex === draft.brand!.toUpperCase());
  const hexInvalid = hexText.trim() !== '' && !isBrandHex(hexText.trim());

  const save = async (next: StoreTheme | null, kind: 'save' | 'inherit') => {
    setSaving(kind);
    setStatus(null);
    try {
      const saved = await onSave(next ? normalizeTheme(next) : null);
      const v = normalizeTheme(saved) || {};
      setDraft(v);
      setHexText(v.brand || '');
      setStatus({ kind: 'ok', text: t('settings:theme.saved') });
    } catch (err: any) {
      setStatus({ kind: 'error', text: err?.message || t('settings:theme.saveError') });
    } finally {
      setSaving(null);
    }
  };

  // Προεπισκόπηση: ΟΛΑ τα tokens πάνω στο κουτί (όποιο θέμα κι αν ισχύει στη σελίδα γύρω του).
  const previewScope = `[data-theme-preview="${uid}"]`;
  const previewCss = useMemo(() => deriveThemeCss(effective, { scope: previewScope }), [effective, previewScope]);
  const effectiveBrand = effective.brand || DEFAULT_BRAND;

  const titleId = `${uid}-title`;
  const legend = 'text-sm font-semibold text-t1';
  const hint = 'text-xs text-t3 mt-0.5 mb-2';

  return (
    <section className="mt-6 pt-6 border-t border-line" data-testid={`theme-section-${scope}`} aria-labelledby={titleId}>
      <div className="mb-4">
        <h3 id={titleId} className="text-sm font-semibold text-t1 mb-1 flex items-center gap-2">
          <Palette className="h-4 w-4 text-brand" />
          {t('settings:theme.title')}
        </h3>
        <p className="text-xs text-t4">{isStore ? t('settings:theme.subtitleStore') : t('admin:orgThemeHint')}</p>
      </div>

      <div className="grid grid-cols-1 gap-6">
        <div className="space-y-6 min-w-0">
          {!canEdit && (
            <p className="text-xs text-t3 rounded-lg border border-line bg-surface-1 px-3 py-2">{t('settings:theme.readOnly')}</p>
          )}

          {/* Χρώμα */}
          <fieldset disabled={disabled} className="min-w-0">
            <legend className={legend}>{t('settings:theme.brandLabel')}</legend>
            <p className={hint}>{t('settings:theme.brandHint')} {t('settings:theme.brandNote')}</p>
            {isStore && (
              <Choice name={`${uid}-brand`} checked={!draft.brand} disabled={disabled} onSelect={() => setBrand(null)}
                className="mb-2" testId="theme-brand-inherit">
                <span aria-hidden="true" className="w-5 h-5 rounded-md border border-line shrink-0"
                  style={{ background: parent?.brand || DEFAULT_BRAND }} />
                <span>{inheritLabel}</span>
                <span className="text-t3 font-normal">· {colorLabel(parent?.brand)}</span>
              </Choice>
            )}
            <div className="grid grid-cols-6 gap-2 max-w-[22rem]" role="presentation">
              {BRAND_SWATCHES.map(sw => {
                const checked = brandChecked(sw.hex);
                return (
                  <label key={sw.id} className={`relative ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`} title={`${swatchName[sw.id]} ${sw.hex}`}>
                    <input type="radio" name={`${uid}-brand`} className="peer sr-only" checked={checked} disabled={disabled}
                      aria-label={swatchName[sw.id]} onChange={() => setBrand(brandForSwatch(sw.hex))} />
                    <span aria-hidden="true"
                      className={`flex items-center justify-center w-11 h-11 rounded-xl border border-black/10 transition-transform
                        peer-focus-visible:ring-2 peer-focus-visible:ring-t1 peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-surface-2
                        ${checked ? 'ring-2 ring-t1 ring-offset-2 ring-offset-surface-2' : 'hover:scale-105'}`}
                      style={{ background: sw.hex }}>
                      {checked && <Check className="w-5 h-5" strokeWidth={3} style={{ color: swatchCheck(sw.hex) }} />}
                    </span>
                  </label>
                );
              })}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <label className={`relative w-11 h-11 rounded-xl border overflow-hidden shrink-0 ${customBrand ? 'border-brand ring-2 ring-t1 ring-offset-2 ring-offset-surface-2' : 'border-line'}`}
                title={t('settings:theme.customColor')}>
                <input type="color" aria-label={t('settings:theme.pickColor')}
                  className="absolute inset-0 w-full h-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
                  value={(draft.brand || parent?.brand || DEFAULT_BRAND).toLowerCase()}
                  disabled={disabled}
                  onChange={e => setBrand(e.target.value.toUpperCase())} />
                <span aria-hidden="true" className="block w-full h-full" style={{ background: draft.brand || parent?.brand || DEFAULT_BRAND }} />
              </label>
              <input
                type="text"
                inputMode="text"
                spellCheck={false}
                maxLength={7}
                aria-label={t('settings:theme.hexLabel')}
                aria-invalid={hexInvalid || undefined}
                placeholder="#F5B700"
                value={hexText}
                disabled={disabled}
                onChange={e => {
                  const v = e.target.value.trim();
                  setHexText(v);
                  if (isBrandHex(v)) set({ brand: v.toUpperCase() });
                }}
                className={`h-11 w-32 px-3 rounded-xl border bg-surface-2 text-t1 font-mono text-sm uppercase placeholder-t4
                  focus:outline-none focus:ring-2 focus:ring-brand ${hexInvalid ? 'border-red-500' : 'border-line-strong'}`}
              />
              <span className="text-xs text-t3">{colorLabel(effective.brand)}</span>
            </div>
            {hexInvalid && <p className="text-xs text-red-600 dark:text-red-400 mt-1" role="alert">{t('settings:theme.hexInvalid')}</p>}
          </fieldset>

          {/* Φόντο */}
          <fieldset disabled={disabled} className="min-w-0">
            <legend className={legend}>{t('settings:theme.surfaceLabel')}</legend>
            <p className={hint}>{t('settings:theme.surfaceHint')} {t('settings:theme.surfaceNote')}</p>
            <div className="grid gap-2 grid-cols-1 sm:grid-cols-2">
              {isStore && (
                <Choice name={`${uid}-surface`} checked={!draft.surface} disabled={disabled} onSelect={() => set({ surface: null })}
                  className="sm:col-span-2" testId="theme-surface-inherit">
                  <span>{inheritLabel}</span>
                  <span className="text-t3 font-normal">· {surfaceName[parent?.surface || DEFAULT_SURFACE]}</span>
                </Choice>
              )}
              {THEME_SURFACES.map(s => {
                const [c0, c1, c2] = tonePreview(effectiveBrand, s);
                return (
                  <Choice key={s} name={`${uid}-surface`} checked={surfaceChecked(s)} disabled={disabled}
                    onSelect={() => set({ surface: surfaceForChoice(s) })}>
                    <span aria-hidden="true" className="relative w-11 h-8 rounded-md border border-black/10 overflow-hidden shrink-0" style={{ background: c0 }}>
                      <span className="absolute left-1.5 top-1.5 right-0 bottom-0 rounded-tl" style={{ background: c1 }} />
                      <span className="absolute left-3.5 top-3.5 right-1 bottom-1 rounded-sm" style={{ background: c2 }} />
                    </span>
                    <span className="min-w-0">
                      <span className="block leading-tight">{surfaceName[s]}</span>
                      <span className="block text-[11px] text-t3 font-normal leading-tight">{surfaceHint[s]}</span>
                    </span>
                  </Choice>
                );
              })}
            </div>
          </fieldset>

          {/* Ανοιχτό / σκούρο */}
          <fieldset disabled={disabled} className="min-w-0">
            <legend className={legend}>{t('settings:theme.appearanceLabel')}</legend>
            <p className={hint}>{t('settings:theme.appearanceHint')}</p>
            <Choice name={`${uid}-appearance`} checked={!draft.appearance} disabled={disabled} onSelect={() => set({ appearance: null })}
              className="mb-2" testId="theme-appearance-inherit">
              <span>{isStore ? inheritLabel : t('settings:theme.appearance.app')}</span>
              {isStore && parent?.appearance && <span className="text-t3 font-normal">· {appearanceName[parent.appearance]}</span>}
            </Choice>
            <div className="grid grid-cols-3 gap-2">
              {APPEARANCES.map(a => {
                const Icon = APPEARANCE_ICON[a];
                return (
                  <Choice key={a} name={`${uid}-appearance`} checked={draft.appearance === a} disabled={disabled}
                    onSelect={() => set({ appearance: a })}>
                    <Icon aria-hidden="true" className="w-4 h-4 shrink-0" />
                    <span>{appearanceName[a]}</span>
                  </Choice>
                );
              })}
            </div>
          </fieldset>
        </div>

        {/* Προεπισκόπηση */}
        <div className="min-w-0">
          <p className={legend}>{t('settings:theme.previewLabel')}</p>
          <p className={hint}>{t('settings:theme.previewHint')}</p>
          <style data-testid="theme-preview-css">{previewCss}</style>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {(['light', 'dark'] as const).map(mode => (
              <div key={mode} className="min-w-0">
                <p className="text-[11px] font-medium text-t3 mb-1">{mode === 'light' ? appearanceName.light : appearanceName.dark}</p>
                <CashierPreview scopeId={uid} mode={mode} storeName={storeName} testId={`theme-preview-${mode}`}
                  className="rounded-xl border border-line overflow-hidden" />
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mt-5">
        <button type="button" aria-label={t('settings:theme.saveAria')} disabled={disabled || !dirty || hexInvalid}
          onClick={() => save(draft, 'save')}
          className="inline-flex items-center justify-center gap-2 min-h-[44px] px-5 rounded-xl text-sm font-semibold bg-brand text-brand-fg hover:brightness-110 active:scale-[0.98] transition disabled:opacity-50 disabled:cursor-not-allowed">
          {saving === 'save' && <Loader2 className="w-4 h-4 animate-spin" />}
          {t('settings:theme.save')}
        </button>
        <button type="button" disabled={disabled || (normalizeTheme(value) === null && !dirty)}
          onClick={() => save(null, 'inherit')}
          className="inline-flex items-center justify-center gap-2 min-h-[44px] px-4 rounded-xl text-sm font-medium border border-line-strong bg-surface-2 text-t1 hover:bg-surface-3 transition disabled:opacity-50 disabled:cursor-not-allowed">
          {saving === 'inherit' && <Loader2 className="w-4 h-4 animate-spin" />}
          {isStore ? (hasOrg ? t('settings:theme.useOrg') : t('settings:theme.useApp')) : t('admin:orgThemeReset')}
        </button>
        {loading && <Loader2 className="w-4 h-4 animate-spin text-t3" aria-label={t('settings:theme.loading')} />}
        <p className="basis-full text-xs text-t3" role="status">
          {status ? (
            <span className={status.kind === 'error' ? 'text-red-600 dark:text-red-400' : 'text-t2'}>{status.text}</span>
          ) : dirty ? t('settings:theme.unsaved') : t('settings:theme.applyHint')}
        </p>
      </div>
    </section>
  );
};

const ThemeSettingsSection: React.FC<ThemeSettingsSectionProps> = props => (
  props.variant === 'sheet' ? <ThemeSheet {...props} /> : <ThemeSelfSaving {...props} />
);

export default ThemeSettingsSection;

/* ───────────────────────── δεδομένα καταστήματος ───────────────────────── */

interface StoreThemePayload {
  theme?: ResolvedStoreTheme | null;
  themeOverride?: StoreTheme | null;
  themeOrg?: StoreTheme | null;
  canEdit?: boolean;
}

/** Τα πεδία του store (useAuth) που αφορούν το θέμα· ενημερώνονται επί τόπου μετά την αποθήκευση. */
interface StoreThemeHolder {
  id?: number;
  theme?: ResolvedStoreTheme | null;
  themeOverride?: StoreTheme | null;
  themeOrg?: StoreTheme | null;
}

export interface StoreThemeForm {
  /** Το πρόχειρο (useDirtyForm: αποθηκευμένο/τρέχον, «Αναίρεση», φρουρός «έχεις αλλαγές»). */
  form: DirtyForm<ThemeDraft>;
  org: StoreTheme | null;
  canEdit: boolean;
  loading: boolean;
  saving: boolean;
  /** PUT /api/stores/:id/theme με όλο το πρόχειρο (null = όλα κληρονομούνται). Error με μήνυμα σε αποτυχία. */
  save: () => Promise<void>;
}

const authHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('xentrix_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Κατάστημα → «Εμφάνιση»: το θέμα ως φόρμα της σελίδας. Ξεκινά από ό,τι έφερε το /auth/me και ξαναδιαβάζει
 * από τον server (μπορεί να το άλλαξε άλλος διαχειριστής). Μετά την αποθήκευση ισχύει αμέσως σε αυτή τη
 * συσκευή (applyStoreTheme)· τα ανοιχτά ταμεία μετά από F5.
 */
export function useStoreThemeForm(store: StoreThemeHolder | null | undefined, guardId: string): StoreThemeForm {
  const { t } = useTranslation(['settings']);
  const storeId = store?.id;
  const form = useDirtyForm<ThemeDraft>(toThemeDraft(store?.themeOverride), { guardId });
  const { reset, markSaved } = form;
  const [org, setOrg] = useState<StoreTheme | null>(() => normalizeTheme(store?.themeOrg));
  const [canEdit, setCanEdit] = useState(true);
  const [loading, setLoading] = useState(storeId != null);
  const [saving, setSaving] = useState(false);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  useEffect(() => {
    if (!storeId) {
      setLoading(false);
      return undefined;
    }
    let live = true;
    setLoading(true);
    fetch(`${API_BASE_URL}/api/stores/${storeId}/theme`, { headers: authHeaders() })
      .then(async res => (res.ok ? ((await res.json()) as StoreThemePayload) : null))
      .then(data => {
        if (!live || !data) return;
        if ('themeOverride' in data) reset(toThemeDraft(data.themeOverride));
        if ('themeOrg' in data) setOrg(normalizeTheme(data.themeOrg));
        if (typeof data.canEdit === 'boolean') setCanEdit(data.canEdit);
      })
      .catch(() => { /* μένει ό,τι ήρθε με το /auth/me */ })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [storeId, reset]);

  const save = async (): Promise<void> => {
    if (!storeId) return;
    const next = normalizeTheme(form.values);
    setSaving(true);
    try {
      // Χωρίς δίκτυο το fetch ρίχνει «Failed to fetch» (αγγλικά): ο χρήστης βλέπει το δικό μας μήνυμα.
      const res = await fetch(`${API_BASE_URL}/api/stores/${storeId}/theme`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ theme: next }),
      }).catch(() => { throw new Error(t('settings:theme.saveError')); });
      const body = (await res.json().catch(() => null)) as (StoreThemePayload & { error?: string; message?: string }) | null;
      if (!res.ok) throw new Error(body?.message || body?.error || t('settings:theme.saveError'));
      const saved = normalizeTheme(body?.themeOverride ?? next);
      const nextOrg = body && 'themeOrg' in body ? normalizeTheme(body.themeOrg) : org;
      if (body && 'themeOrg' in body) setOrg(nextOrg);
      markSaved(toThemeDraft(saved));
      const applied = body?.theme ?? resolveTheme(nextOrg, saved);
      applyStoreTheme(applied);
      // Όπως τα στοιχεία του καταστήματος (useStoreSettingsForm): ό,τι διαβάσει store.theme* βλέπει το νέο.
      if (store) Object.assign(store, { themeOverride: saved, theme: applied });
    } finally {
      if (alive.current) setSaving(false);
    }
  };

  return { form, org, canEdit, loading, saving, save };
}
